import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, BACKEND_URL } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SectionTitle } from "@/components/dashboard/widgets";
import { Loader2, Copy, ExternalLink, Layout, Filter, Sparkles } from "lucide-react";
import { copyToClipboard } from "@/lib/clipboard";
import { toast } from "sonner";

/**
 * Affiliate Landers — browse live publisher landers and grab a tracking link.
 *
 * Affiliates can promote any active publisher lander. Adding `?aff=<short>` to
 * the URL stamps the lead with the affiliate's id at submission time so the
 * commission can be reconciled by finance / admin.
 */
export default function Landers() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sector, setSector] = useState("all");
  const [customSub, setCustomSub] = useState("");

  const affId = (user?.id || "").slice(0, 8);

  useEffect(() => {
    setLoading(true);
    api.get(`/public/landers?limit=200${sector !== "all" ? `&sector=${sector}` : ""}`)
      .then((r) => setRows(r.data || []))
      .finally(() => setLoading(false));
  }, [sector]);

  const sectors = useMemo(() => {
    const s = new Set(rows.map((r) => r.sector));
    return ["all", ...Array.from(s)];
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.headline || "").toLowerCase().includes(q)
      || (r.publisher_company || "").toLowerCase().includes(q)
      || (r.slug || "").toLowerCase().includes(q),
    );
  }, [rows, search]);

  const buildUrl = (slug) => {
    const params = new URLSearchParams();
    params.set("aff", affId);
    if (customSub.trim()) params.set("sub_id", customSub.trim());
    return `${BACKEND_URL}/l/${slug}?${params.toString()}`;
  };

  return (
    <div className="space-y-6" data-testid="affiliate-landers">
      <SectionTitle
        eyebrow="Affiliate landers"
        title="Promote any live lander"
        subtitle={<>Pick any publisher lander, grab your tracking URL, and earn commission on every signed-up lead. Your affiliate short-id <code className="font-mono text-[12px] text-[#0055FF] bg-[#0055FF]/[0.06] px-1.5 py-0.5 rounded">{affId}</code> is appended automatically.</>}
      />

      <div className="grid sm:grid-cols-[1fr_auto_auto] gap-3 items-end">
        <div className="space-y-1.5">
          <label className="text-[11.5px] text-slate-500">Search by headline / publisher / slug</label>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="e.g. Mortgages, Sterling Demand…" data-testid="aff-landers-search" />
        </div>
        <div className="space-y-1.5">
          <label className="text-[11.5px] text-slate-500 flex items-center gap-1"><Filter className="size-3" /> Sector</label>
          <select
            value={sector} onChange={(e) => setSector(e.target.value)}
            className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm capitalize"
            data-testid="aff-landers-sector"
          >
            {sectors.map((s) => <option key={s} value={s}>{s === "all" ? "All sectors" : s.replace("-", " ")}</option>)}
          </select>
        </div>
        <div className="space-y-1.5">
          <label className="text-[11.5px] text-slate-500">Optional sub-id</label>
          <Input value={customSub} onChange={(e) => setCustomSub(e.target.value)} placeholder="e.g. linkedin-q1" className="font-mono text-sm" data-testid="aff-landers-subid" />
        </div>
      </div>

      {loading ? (
        <div className="grid place-items-center py-20"><Loader2 className="size-5 text-slate-400 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <Card className="rounded-xl border-dashed border-slate-300 bg-slate-50/40">
          <CardContent className="p-10 text-center">
            <Sparkles className="size-5 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 text-sm">No live landers match your filters yet. Check back soon — publishers are spinning new ones up daily.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {filtered.map((l) => {
            const trackingUrl = buildUrl(l.slug);
            const primary = l.theme?.primary || "#0055FF";
            return (
              <Card key={l.slug} className="rounded-xl border-slate-200 shadow-soft" data-testid={`aff-lander-${l.slug}`}>
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div
                      className="size-10 shrink-0 rounded-lg grid place-items-center"
                      style={{ background: `color-mix(in srgb, ${primary} 12%, transparent)`, color: primary }}
                    >
                      <Layout className="size-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-slate-900 truncate">{l.headline}</h3>
                        <span className="text-[10.5px] uppercase tracking-widest text-slate-500">{(l.sector || "").replace("-", " ")}</span>
                      </div>
                      <p className="text-[13.5px] text-slate-500 mt-1 line-clamp-2">{l.subheadline}</p>
                      <div className="mt-2 text-[11.5px] text-slate-400">By <span className="text-slate-600">{l.publisher_company || "Connexis Partner"}</span></div>
                      <div className="mt-4 grid sm:grid-cols-[1fr_auto] gap-2 items-center">
                        <Input value={trackingUrl} readOnly className="font-mono text-[12px] text-slate-600" data-testid={`aff-lander-url-${l.slug}`} />
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" className="rounded-full" onClick={() => copyToClipboard(trackingUrl, "Tracking link copied")} data-testid={`aff-lander-copy-${l.slug}`}>
                            <Copy className="size-3.5" /> Copy
                          </Button>
                          <Button variant="outline" size="sm" className="rounded-full" asChild>
                            <a href={trackingUrl} target="_blank" rel="noreferrer" data-testid={`aff-lander-open-${l.slug}`}>
                              <ExternalLink className="size-3.5" /> Open
                            </a>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
